import { useState, useRef } from 'react';
import {
  useCompanies,
  useCompanyAssets,
  useUploadCompanyAsset,
  useDeleteCompanyAsset,
  useSetDefaultCompanyAsset,
} from '../../hooks/unified/useCompaniesQueries';
import { useToast } from '../../contexts/ToastContext';
import { env } from '../../config/env';
import type { CompanyAsset } from '../../services/unified/types';

interface CompanyAssetLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetSelected: (assetUrl: string, assetType: 'logo' | 'image') => void;
  assetType?: 'logo' | 'image' | 'both';
}

export function CompanyAssetLibrary({
  isOpen,
  onClose,
  onAssetSelected,
  assetType = 'both',
}: CompanyAssetLibraryProps) {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [uploadingAssetType, setUploadingAssetType] = useState<'logo' | 'image' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();

  // Fetch companies
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();

  // Fetch assets for selected company
  const { data: assets = [], isLoading: assetsLoading } = useCompanyAssets(
    selectedCompanyId || ''
  );

  // Mutations
  const uploadMutation = useUploadCompanyAsset();
  const deleteMutation = useDeleteCompanyAsset();
  const setDefaultMutation = useSetDefaultCompanyAsset();

  // Filter assets by type if specified
  const filteredAssets = assets.filter((asset) => {
    if (assetType === 'both') return true;
    return asset.asset_type === assetType;
  });

  // Helper function to get full asset URL
  const getAssetUrl = (asset: CompanyAsset): string => {
    // If URL is already absolute, return as is
    if (asset.url.startsWith('http://') || asset.url.startsWith('https://')) {
      return asset.url;
    }
    // Otherwise, prepend API base URL
    return `${env.API_BASE_URL}${asset.url}`;
  };

  // Handle company selection
  const handleCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCompanyId || !uploadingAssetType) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Invalid file type', 'Please upload a PNG, JPG, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File too large', 'Maximum file size is 5MB');
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        companyId: selectedCompanyId,
        file,
        assetType: uploadingAssetType,
        isDefault: false,
      });

      showSuccess('Asset uploaded successfully');
      setUploadingAssetType(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      showError('Upload failed', 'Failed to upload asset');
      console.error('Asset upload error:', error);
    }
  };

  // Handle asset selection
  const handleAssetClick = (asset: CompanyAsset) => {
    onAssetSelected(asset.url, asset.asset_type);
    showSuccess('Asset selected', `${asset.asset_type === 'logo' ? 'Logo' : 'Image'} has been applied`);
    onClose();
  };

  // Handle delete asset
  const handleDeleteAsset = async (asset: CompanyAsset) => {
    if (!confirm(`Are you sure you want to delete this ${asset.asset_type}?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        companyId: asset.company_id,
        assetId: asset.id,
      });

      showSuccess('Asset deleted successfully');
    } catch (error) {
      showError('Delete failed', 'Failed to delete asset');
      console.error('Asset delete error:', error);
    }
  };

  // Handle set default asset
  const handleSetDefault = async (asset: CompanyAsset) => {
    try {
      await setDefaultMutation.mutateAsync({
        companyId: asset.company_id,
        assetId: asset.id,
      });

      showSuccess('Default asset updated');
    } catch (error) {
      showError('Update failed', 'Failed to set default asset');
      console.error('Set default error:', error);
    }
  };

  // Trigger file upload
  const triggerUpload = (type: 'logo' | 'image') => {
    if (!selectedCompanyId) {
      showError('No company selected', 'Please select a company first');
      return;
    }
    setUploadingAssetType(type);
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Company Asset Library</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Company Sidebar */}
            <div className="w-64 border-r border-gray-200 overflow-y-auto bg-gray-50">
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Companies</h3>
                {companiesLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : companies.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No companies found</p>
                ) : (
                  <div className="space-y-1">
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => handleCompanySelect(company.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedCompanyId === company.id
                            ? 'bg-primary-100 text-primary-900 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {company.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Asset Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {!selectedCompanyId ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Select a company</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Choose a company from the sidebar to view its assets
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Upload Buttons */}
                  <div className="mb-6 flex items-center space-x-3">
                    {(assetType === 'logo' || assetType === 'both') && (
                      <button
                        onClick={() => triggerUpload('logo')}
                        disabled={uploadMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Upload Logo
                      </button>
                    )}
                    {(assetType === 'image' || assetType === 'both') && (
                      <button
                        onClick={() => triggerUpload('image')}
                        disabled={uploadMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Upload Image
                      </button>
                    )}
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileSelect}
                  />

                  {/* Assets Grid */}
                  {assetsLoading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : filteredAssets.length === 0 ? (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No assets</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Upload your first {assetType === 'logo' ? 'logo' : assetType === 'image' ? 'image' : 'asset'} to get started
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredAssets.map((asset) => (
                        <div
                          key={asset.id}
                          className="group relative border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-white"
                        >
                          {/* Asset Image */}
                          <div
                            className="aspect-square bg-gray-50 flex items-center justify-center cursor-pointer"
                            onClick={() => handleAssetClick(asset)}
                          >
                            <img
                              src={getAssetUrl(asset)}
                              alt={asset.filename}
                              className="max-w-full max-h-full object-contain p-2"
                              onError={(e) => {
                                // Fallback for broken images
                                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          </div>

                          {/* Asset Info */}
                          <div className="p-3 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  asset.asset_type === 'logo'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {asset.asset_type}
                              </span>
                              {asset.is_default && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate" title={asset.filename}>
                              {asset.filename}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {(asset.file_size / 1024).toFixed(1)} KB
                            </p>
                          </div>

                          {/* Action Buttons (shown on hover) */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                            {!asset.is_default && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetDefault(asset);
                                }}
                                disabled={setDefaultMutation.isPending}
                                className="p-1.5 bg-white rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
                                title="Set as default"
                              >
                                <svg
                                  className="w-4 h-4 text-yellow-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                  />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAsset(asset);
                              }}
                              disabled={deleteMutation.isPending}
                              className="p-1.5 bg-white rounded-md shadow-sm hover:bg-red-50 disabled:opacity-50"
                              title="Delete asset"
                            >
                              <svg
                                className="w-4 h-4 text-red-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
